export const MODULE_CATEGORIES = {
  AI_GROWTH: "ai-growth",
  CONTENT: "content",
  AI_TOOLS: "ai-tools",
  PLAYGROUND: "playground",
  AIX_INFRA: "aix-infra",
  SUPER_ADMIN: "super-admin",
} as const

export type ModuleCategory = (typeof MODULE_CATEGORIES)[keyof typeof MODULE_CATEGORIES]

// Map routes to their categories (based on actual routes in the app)
export const ROUTE_CATEGORY_MAP: Record<string, ModuleCategory> = {
  // AI Growth - AI-powered growth tools
  "/crm": MODULE_CATEGORIES.AI_GROWTH,
  "/seo": MODULE_CATEGORIES.AI_GROWTH,
  "/reports": MODULE_CATEGORIES.AI_GROWTH,
  "/email-marketing": MODULE_CATEGORIES.AI_GROWTH,
  "/reddit-growth": MODULE_CATEGORIES.AI_GROWTH,

  // Content - Content creation and management
  "/content-ingestion": MODULE_CATEGORIES.CONTENT,
  "/content-management": MODULE_CATEGORIES.CONTENT,
  "/newsletter": MODULE_CATEGORIES.CONTENT,

  // AI Tools - General AI utilities
  "/diagrammer": MODULE_CATEGORIES.AI_TOOLS,
  "/image-converter": MODULE_CATEGORIES.AI_TOOLS,
  "/calendar-parser": MODULE_CATEGORIES.AI_TOOLS,
  "/multi-language-test": MODULE_CATEGORIES.AI_TOOLS,
  "/databrain": MODULE_CATEGORIES.AI_TOOLS,
  "/llm-playground": MODULE_CATEGORIES.AI_TOOLS,
  "/ai-gateway": MODULE_CATEGORIES.AI_TOOLS,
  "/linkpacks": MODULE_CATEGORIES.AI_TOOLS,
  "/aura": MODULE_CATEGORIES.AI_TOOLS,
  "/aura/templates": MODULE_CATEGORIES.AI_TOOLS,
  "/aura/template-builder": MODULE_CATEGORIES.AI_TOOLS,

  // AI Projects - AI-powered product features
  "/sports-wordle": MODULE_CATEGORIES.PLAYGROUND,
  "/eas-dashboard": MODULE_CATEGORIES.PLAYGROUND,
  "/onboarding/journeys": MODULE_CATEGORIES.PLAYGROUND,
  "/onboarding/admin/videos": MODULE_CATEGORIES.PLAYGROUND,
  "/onboarding/admin/journeys": MODULE_CATEGORIES.PLAYGROUND,
  "/onboarding/admin/questions": MODULE_CATEGORIES.PLAYGROUND,
  "/coach-os": MODULE_CATEGORIES.PLAYGROUND,
  "/trackables": MODULE_CATEGORIES.PLAYGROUND,
  "/movies": MODULE_CATEGORIES.PLAYGROUND,
  "/music": MODULE_CATEGORIES.PLAYGROUND,
  "/mashup": MODULE_CATEGORIES.PLAYGROUND,
  "/children-books": MODULE_CATEGORIES.PLAYGROUND,

  "/ai-chat": MODULE_CATEGORIES.PLAYGROUND,
  "/ai-projects": MODULE_CATEGORIES.PLAYGROUND,
  "/shrays-hobbies": MODULE_CATEGORIES.PLAYGROUND,

  // AIX Infra - Infrastructure and system tools
  "/agents": MODULE_CATEGORIES.AIX_INFRA,
  "/agents/runs": MODULE_CATEGORIES.AIX_INFRA,
  "/evals": MODULE_CATEGORIES.AIX_INFRA,
  "/integrations": MODULE_CATEGORIES.AIX_INFRA,
  "/metrics": MODULE_CATEGORIES.AIX_INFRA,
  "/notifications": MODULE_CATEGORIES.AIX_INFRA,
  "/scraping": MODULE_CATEGORIES.AIX_INFRA,
  "/brain": MODULE_CATEGORIES.AIX_INFRA,
  "/cron": MODULE_CATEGORIES.AIX_INFRA,
  "/devtools/monitors": MODULE_CATEGORIES.AIX_INFRA,
  "/devtools/uptime": MODULE_CATEGORIES.AIX_INFRA,
  "/devtools/vendor-status": MODULE_CATEGORIES.AIX_INFRA,
  "/devtools/deploy-feed": MODULE_CATEGORIES.AIX_INFRA,
  "/devtools/incidents": MODULE_CATEGORIES.AIX_INFRA,
  "/devtools/on-call": MODULE_CATEGORIES.AIX_INFRA,
  "/devtools/secrets": MODULE_CATEGORIES.AIX_INFRA,

  // Super Admin
  "/user-management": MODULE_CATEGORIES.SUPER_ADMIN,
  "/companies": MODULE_CATEGORIES.SUPER_ADMIN,
  "/audit-logs": MODULE_CATEGORIES.SUPER_ADMIN,
}

/** A route's category: exact entry, else its longest listed parent, so detail pages (`/devtools/uptime/$id`) are
 *  gated like their list page instead of only needing sign-in */
export const routeCategoryFor = (pathname: string): ModuleCategory | undefined => {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname
  if (ROUTE_CATEGORY_MAP[path]) return ROUTE_CATEGORY_MAP[path]
  const parent = Object.keys(ROUTE_CATEGORY_MAP)
    .filter((route) => route !== "/" && path.startsWith(`${route}/`))
    .sort((a, b) => b.length - a.length)[0]
  return parent ? ROUTE_CATEGORY_MAP[parent] : undefined
}

// Navigation structure (based on actual routes in the app)
export const navigationCategories = [
  {
    category: MODULE_CATEGORIES.AIX_INFRA,
    label: "AIX Infra",
    items: [
      { to: "/agents", label: "Agents", icon: "🤖" },
      { to: "/agents/runs", label: "Agent runs", icon: "📜" },
      { to: "/evals", label: "Evals", icon: "🧪" },
      { to: "/devtools/monitors", label: "API Monitors", icon: "🔍" },
      { to: "/devtools/uptime", label: "Uptime Monitors", icon: "📈" },
      { to: "/devtools/vendor-status", label: "Vendor Status", icon: "🏢" },
      { to: "/devtools/deploy-feed", label: "Deploy Feed", icon: "🚀" },
      { to: "/devtools/incidents", label: "Incidents", icon: "🚨" },
      { to: "/devtools/on-call", label: "On-call", icon: "📟" },
      { to: "/devtools/secrets", label: "Secrets", icon: "🔑" },
      { to: "/integrations", label: "Integrations", icon: "🔗" },
      { to: "/brain", label: "Brain Tasks", icon: "🧠" },
      { to: "/cron", label: "Scheduled Jobs", icon: "⏰" },
    ],
  },
  {
    category: MODULE_CATEGORIES.AI_GROWTH,
    label: "AIX Growth",
    items: [
      { to: "/crm", label: "CRM", icon: "👥" },
      { to: "/seo", label: "SEO Board", icon: "🔍" },
      { to: "/reports", label: "AIX Reports", icon: "📈" },
      { to: "/reddit-growth", label: "Reddit Growth", icon: "📈" },
    ],
  },
  {
    category: MODULE_CATEGORIES.CONTENT,
    label: "Content Gen",
    items: [
      { to: "/content-ingestion", label: "Content Ingestion", icon: "📥" },
      { to: "/content-management", label: "Content Management", icon: "📝" },
      { to: "/newsletter", label: "Newsletter", icon: "📧" },
    ],
  },
  {
    category: MODULE_CATEGORIES.AI_TOOLS,
    label: "AI Tools",
    items: [
      { to: "/ai-gateway", label: "AI Gateway", icon: "🛡️" },
      { to: "/llm-playground", label: "LLM Playground", icon: "🧪" },
      {
        label: "Aura Builder",
        icon: "✨",
        subItems: [
          { to: "/aura", label: "Projects", icon: "📁" },
          { to: "/aura/templates", label: "Templates", icon: "📋" },
          { to: "/aura/template-builder", label: "Template Builder", icon: "🛠️" },
        ],
      },
      { to: "/diagrammer", label: "Diagrammer", icon: "🗺️" },
      { to: "/image-converter", label: "Image Converter", icon: "🎨" },
      { to: "/calendar-parser", label: "Calendar Parser", icon: "📅" },
      { to: "/multi-language-test", label: "Multi-Language Test", icon: "🚀" },
      { to: "/databrain", label: "Database Explorer", icon: "🗄️" },
      { to: "/linkpacks", label: "LinkPacks", icon: "🗂️" },
    ],
  },
  {
    category: MODULE_CATEGORIES.PLAYGROUND,
    label: "Playground",
    items: [
      { to: "/eas-dashboard", label: "EAS Updates", icon: "📱" },
      {
        label: "Video Onboarding",
        icon: "🎓",
        subItems: [
          { to: "/onboarding/journeys", label: "Learning Journeys", icon: "🗺️" },
          { to: "/onboarding/admin/videos", label: "Manage Videos", icon: "📹" },
          { to: "/onboarding/admin/journeys", label: "Manage Journeys", icon: "✏️" },
          { to: "/onboarding/admin/questions", label: "Questions Queue", icon: "❓" },
        ],
      },
      {
        label: "CoachOS",
        icon: "🏃",
        subItems: [
          { to: "/coach-os", label: "Coach", icon: "🏃" },
          { to: "/trackables", label: "Trackables Admin", icon: "📊" },
        ],
      },
      { to: "/movies", label: "Movie Discovery", icon: "🎬" },
      { to: "/music", label: "Music", icon: "🎵" },
      { to: "/mashup", label: "DJ Mashup", icon: "🎧" },
      { to: "/children-books", label: "Children Books", icon: "📚" },
      { to: "/sports-wordle", label: "Sports Wordle", icon: "🏀" },
      { to: "/shrays-hobbies", label: "Shray's Hobbies", icon: "🎯" },
      {
        label: "AI Chat",
        icon: "🤖",
        subItems: [
          { to: "/ai-projects", label: "Projects", icon: "📁" },
          { to: "/ai-chat", label: "Chat Hub", icon: "💬" },
        ],
      },
    ],
  },
  {
    category: MODULE_CATEGORIES.SUPER_ADMIN,
    label: "Super Admin",
    items: [
      { to: "/user-management", label: "User Management", icon: "👤" },
      { to: "/companies", label: "Companies", icon: "🏢" },
      { to: "/audit-logs", label: "Audit Logs", icon: "📋" },
    ],
  },
]

// System items (always visible)
export const systemItems = [
  { to: "/", label: "📊 Home" },
  { to: "/tools", label: "🛠️ Tools" },
  { to: "/settings", label: "🔐 Settings" },
  { to: "/electron-native", label: "⚡ Electron Native" },
]

// Public routes that don't require authentication

// Helper function to check if a route is public

// Helper function to get navigation items for a user based on their categories
export const getNavigationForUser = (userCategories: string[] = [], isSuperAdmin = false) => {
  const filteredCategories = navigationCategories
    .map((category) => {
      // Super admin category is only visible to super admins
      if (category.category === MODULE_CATEGORIES.SUPER_ADMIN) {
        return isSuperAdmin ? category : null
      }

      // For other categories, check if user has access to this category
      const hasAccessToCategory = userCategories.includes(category.category) || isSuperAdmin

      // Return the full category if user has access, otherwise null
      return hasAccessToCategory ? category : null
    })
    .filter(Boolean)

  return {
    categories: filteredCategories,
    systemItems, // System items are always visible
  }
}
